import { useState } from 'react'
import { Head, Link, router, useForm } from '@inertiajs/react'
import { User, Clock, Eye, Calendar, ArrowLeft, MessageCircle, ThumbsUp, Pencil, Trash2, Send, FileText } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { ArticleContent } from '@/components/content/ArticleContent'
import { ArticleRatingWidget } from '@/components/content/ArticleRatingWidget'
import { AttachmentPicker, AttachmentList } from '@/components/ui/AttachmentPicker'
import { linkifyText } from '@/utils/linkify'
import { MentionTextarea } from '@/components/ui/MentionTextarea'
import type { ArticleDetail, CategoryInfo, BreadcrumbItem, ArticleComment } from '@/types'

interface ArticleWithJson extends ArticleDetail {
  body_json?: string | null
}

interface ContentArticleProps {
  article: ArticleWithJson
  category: CategoryInfo
  comments: ArticleComment[]
  breadcrumb: BreadcrumbItem[]
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return "à l'instant"
  if (diffInSeconds < 3600) return `il y a ${Math.floor(diffInSeconds / 60)} min`
  if (diffInSeconds < 86400) return `il y a ${Math.floor(diffInSeconds / 3600)} h`
  if (diffInSeconds < 604800) return `il y a ${Math.floor(diffInSeconds / 86400)} j`

  return formatDate(dateString)
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

interface CommentCardProps {
  comment: ArticleComment
  categorySlug: string
  articleSlug: string
}

function CommentCard({ comment, categorySlug, articleSlug }: CommentCardProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editBody, setEditBody] = useState(comment.body)
  const [isDeleting, setIsDeleting] = useState(false)
  const baseUrl = `/content/${categorySlug}/${articleSlug}/comments`

  const handleUpdate = () => {
    router.patch(`${baseUrl}/${comment.id}`, {
      comment: { body: editBody }
    }, {
      onSuccess: () => setIsEditing(false),
      preserveScroll: true
    })
  }

  const handleDelete = () => {
    router.delete(`${baseUrl}/${comment.id}`, { preserveScroll: true })
  }

  const handleLike = () => {
    if (comment.is_liked) {
      router.delete(`${baseUrl}/${comment.id}/like`, { preserveScroll: true })
    } else {
      router.post(`${baseUrl}/${comment.id}/like`, {}, { preserveScroll: true })
    }
  }

  return (
    <div className="flex gap-3 p-4 rounded-lg bg-gray-50">
      <div className="flex-shrink-0 w-8 h-8 text-xs rounded-full bg-tessan-primary text-white flex items-center justify-center font-medium">
        {getInitials(comment.author.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{comment.author.name}</span>
            {comment.author.specialty && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-sm text-gray-500">{comment.author.specialty}</span>
              </>
            )}
            <span className="text-gray-400">·</span>
            <time className="text-sm text-gray-500" dateTime={comment.created_at}>
              {formatRelativeDate(comment.created_at)}
            </time>
          </div>

          {comment.is_author && !isEditing && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-gray-400 hover:text-tessan-primary transition-colors"
                title="Modifier"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsDeleting(true)}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-3">
            <textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent resize-none"
              rows={3}
            />
            <div className="flex items-center gap-2">
              <button
                onClick={handleUpdate}
                className="px-3 py-1.5 bg-tessan-primary text-white text-sm rounded-lg hover:bg-tessan-primary-hover transition-colors"
              >
                Enregistrer
              </button>
              <button
                onClick={() => {
                  setIsEditing(false)
                  setEditBody(comment.body)
                }}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-700 whitespace-pre-wrap">{linkifyText(comment.body)}</p>

            {comment.attachments && comment.attachments.length > 0 && (
              <div className="mt-2">
                <AttachmentList attachments={comment.attachments} />
              </div>
            )}
          </>
        )}

        {/* Delete confirmation */}
        {isDeleting && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800 mb-2">Supprimer ce commentaire ?</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDelete}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
              <button
                onClick={() => setIsDeleting(false)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {!isEditing && !isDeleting && (
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
            <button
              onClick={handleLike}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all ${
                comment.is_liked
                  ? 'text-tessan-primary bg-tessan-primary/10'
                  : 'hover:text-tessan-primary hover:bg-gray-100'
              }`}
            >
              <ThumbsUp className={`w-4 h-4 ${comment.is_liked ? 'fill-tessan-primary' : ''}`} />
              {comment.likes_count}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function CommentForm({ categorySlug, articleSlug }: { categorySlug: string; articleSlug: string }) {
  const { data, setData, post, processing, reset, errors } = useForm<{
    body: string
    attachments: File[]
  }>({
    body: '',
    attachments: []
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/content/${categorySlug}/${articleSlug}/comments`, {
      forceFormData: data.attachments.length > 0,
      onSuccess: () => reset(),
      preserveScroll: true
    })
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 pt-6 border-t border-gray-200">
      <label htmlFor="article-comment-body" className="block text-sm font-medium text-gray-700 mb-2">
        Ajouter un commentaire
      </label>
      <div className="space-y-3">
        <MentionTextarea
          id="article-comment-body"
          value={data.body}
          onChange={(val) => setData('body', val)}
          placeholder="Votre commentaire... (tapez @ pour mentionner)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent resize-none"
          rows={3}
        />
        {errors.body && (
          <p className="text-sm text-red-600">{errors.body}</p>
        )}
        <AttachmentPicker
          files={data.attachments}
          onFilesChange={(files) => setData('attachments', files)}
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={processing || !data.body.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-tessan-primary text-white rounded-lg hover:bg-tessan-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            Envoyer
          </button>
        </div>
      </div>
    </form>
  )
}

export default function ContentArticle({ article, category, comments, breadcrumb }: ContentArticleProps) {
  return (
    <MainLayout title={article.title}>
      <Head title={`${article.title} - Bibliothèque`} />

      <article>
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb items={breadcrumb} />
        </div>

        {/* Back link */}
        <Link
          href={`/content/${category.slug}`}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-tessan-primary transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à {category.name}
        </Link>

        {/* Header */}
        <header className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {article.title}
          </h1>

          {article.excerpt && (
            <p className="text-lg text-gray-600 mb-6">
              {article.excerpt}
            </p>
          )}

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{article.author.name}</span>
              {article.author.specialty && (
                <span className="text-gray-400">• {article.author.specialty}</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(article.published_at)}</span>
            </div>

            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{article.reading_time} min de lecture</span>
            </div>

            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span>{article.views_count} {article.views_count === 1 ? 'vue' : 'vues'}</span>
            </div>

            {article.comments_enabled && (
              <div className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                <span>{article.comments_count} {article.comments_count === 1 ? 'commentaire' : 'commentaires'}</span>
              </div>
            )}
          </div>
        </header>

        {/* Article body */}
        <div className="prose prose-lg prose-gray max-w-none">
          <ArticleContent
            bodyJson={article.body_json}
            bodyMarkdown={article.body}
          />
        </div>

        {/* Attachments */}
        {article.attachments && article.attachments.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Pièces jointes
            </h3>
            <AttachmentList attachments={article.attachments} />
          </div>
        )}

        {/* Rating widget */}
        <div className="mt-10">
          <ArticleRatingWidget
            articleId={article.id}
            userRating={article.user_rating}
            ratingCounts={article.rating_counts}
            isAuthor={article.is_author}
            isAdmin={article.is_admin}
          />
        </div>

        {/* Comments section */}
        {article.comments_enabled && (
          <section className="mt-10 bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-6">
              Commentaires ({comments.length})
            </h2>

            {comments.length > 0 ? (
              <div className="space-y-4">
                {comments.map((comment) => (
                  <CommentCard
                    key={comment.id}
                    comment={comment}
                    categorySlug={category.slug}
                    articleSlug={article.slug}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Aucun commentaire pour le moment.</p>
                <p className="text-sm">Soyez le premier à réagir !</p>
              </div>
            )}

            <CommentForm categorySlug={category.slug} articleSlug={article.slug} />
          </section>
        )}

        {/* Footer */}
        <footer className="mt-12 pt-6 border-t border-gray-200">
          <Link
            href={`/content/${category.slug}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-tessan-cream text-tessan-primary rounded-lg hover:bg-tessan-primary hover:text-white transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à {category.name}
          </Link>
        </footer>
      </article>
    </MainLayout>
  )
}
